import axios from './axios';
import { getCurrentUserID, getCurrentUserOrgID } from '../utils/userUtils';

export interface BudgetItem {
  budgetItemID: string;
  budgetItemCode: string;
  budgetItemName: string;
  budgetItemBelongBudgetID: string;
  budgetItemParentID: string | null;
  budgetItemLevel: number;
  budgetItemRole: string;
  budgetItemProgressType: string;
  budgetItemCurrency?: string;
  budgetItemUnit1?: string | null;
  budgetItemUnit2?: string | null;
  budgetItemUnitPrice?: number;
  budgetItemQuantity1?: number;
  budgetItemQuantity2?: number;

  budgetItemRelatedID?: string;
  budgetItemOwnerID: string;
  budgetItemAssigneeID: string;
  budgetItemCreatorID: string;
  budgetItemAmount: number;
  budgetItemRemark: string;
  budgetItemUsedAmount: number;
  budgetItemUnusedAmount: number;
  budgetItemExceedingAmount: number;
  budgetItemDataStatus: string;
  budgetItemStatus: string;
  budgetItemCreateTime: string;
  budgetItemUpdateTime: string;
  budgetItemFilmCode: string | null;
  budgetItemChildren?: BudgetItem[];
  budgetItemExpanded?: boolean;
  budgetItemChecked?: boolean;
}

export interface CreateBudgetParams {
  // 必填字段
  budgetID: string;          // 预算ID，最大长度50字符
  
  // 可选字段
  budgetName?: string;       // 预算名称，最大长度100字符
  budgetParentID?: string;   // 上级预算ID，最大长度50字符
  budgetFilmID?: string;     // 关联影片ID，最大长度50字符
  budgetLevel?: '总预算' | '分预算';  // 预算级别
  budgetOwnerID?: string;    // 拥有人ID，最大长度50字符
  budgetAssigneeID?: string; // 负责人ID，最大长度50字符
  budgetPartnersID?: string; // 参与者ID，最大长度50字符
  budgetCreatorID?: string;  // 创建人ID，最大长度50字符
  budgetDataStatus?: string; // 预算生命状态，最大长度255字符
  budgetStatus?: '预算中' | '确认中' | '已生效' | '使用中-未超支' | '使用中-超支';  // 预算状态
  budgetTotalAmount?: number;// 预算总金额，decimal(20,2)
  budgetOverview?: string;   // 预算概述，文本类型
  budgetStorageMode?: '本地' | '在线' | '同步';  // 预算存储位置
  budgetStorageFolderAddress?: string; // 预算存储地址，最大长度400字符
  budgetCreateTime?: string; // 创建时间
  budgetUpdateTime?: string; // 更新时间
  budgetCurrency?: string;   // 预算主货币，最大长度10字符
}

export interface Budget extends CreateBudgetParams {
  budgetCreateTime: string;    // 创建时间
  budgetUpdateTime: string;    // 更新时间
  budgetUsedAmount: number;    // 已使用预算
  budgetUnusedAmount: number;  // 未使用预算
  budgetExceedingAmount: number; // 超出预算
}

// 获取预算列表
export const getBudgets = () => {
  return axios.get<Budget[]>('/api/budgets');
};

// 获取预算详情
export const getBudgetById = (budgetID: string) => {
  return axios.get<Budget>(`/api/budgets/${budgetID}`);
};

// 创建预算
export const createBudget = (params: CreateBudgetParams) => {
  return axios.post<Budget>('/api/budgets', params);
};

// 更新预算
export const updateBudget = (budgetID: string, data: Partial<CreateBudgetParams>) => {
  return axios.put<Budget>(`/api/budgets/${budgetID}`, data);
};

// 删除预算
export const deleteBudget = (budgetID: string) => {
  return axios.delete(`/api/budgets/${budgetID}`);
};

// 获取最近使用的预算
export const getRecentBudgets = (limit: number = 6) => {
  return axios.get<Budget[]>('/api/budgets/recent', { params: { limit } });
};

// 创建预算项
export const createBudgetItem = (params: Partial<BudgetItem>) => {
  return axios.post<BudgetItem>('/api/budgetItems', params);
};

// 更新预算项
export const updateBudgetItem = (budgetItemID: string, data: Partial<BudgetItem>) => {
  return axios.put<BudgetItem>(`/api/budgetItems/${budgetItemID}`, data);
};

// 删除预算项
export const deleteBudgetItem = (budgetItemID: string) => {
  return axios.delete<void>(`/api/budgetItems/${budgetItemID}`);
};

// 获取预算项列表
export const getBudgetItems = (budgetID: string) => {
  return axios.get<BudgetItem[]>(`/api/budgetItems`, {
    params: { budgetItemBelongBudgetID: budgetID }
  });
};

// 批量创建预算项
export const createBudgetItemsBatch = (items: Partial<BudgetItem>[]) => {
  return axios.post<BudgetItem[]>('/api/budgetItems/batch', items);
};

// 批量更新预算项
export const updateBudgetItemsBatch = (items: { budgetItemID: string; data: Partial<BudgetItem> }[]) => {
  return axios.put<BudgetItem[]>('/api/budgetItems/batch', items);
};

// 根据影片ID获取预算列表（列表可能较大，超时长于默认 10s）
export const getBudgetsByFilmId = (filmId: string) => {
  return axios.get(`/api/budgets/film/${filmId}`, { timeout: 30000 });
};

/**
 * 复制预算
 * @param data 复制预算参数，包含原始预算ID和新预算名称
 * @returns 新创建的预算信息
 */
export const copyBudget = (data: {
  sourceBudgetID: string;  // 原始预算ID（必填）
  targetBudgetName?: string;  // 新预算名称（可选）
}) => {
  // 获取用户信息并添加到请求头
  try {
    const userID = getCurrentUserID();
    const userOrgID = getCurrentUserOrgID();
    
    if (userID && userOrgID) {
      return axios.post<Budget>('/api/budgets/copy', data, {
        headers: {
          'X-User-Id': userID,
          'X-User-Org-Id': userOrgID,
        },
      });
    } else {
      throw new Error('用户信息不完整：缺少用户ID或组织ID');
    }
  } catch (error) {
    console.error('获取用户信息失败:', error);
    // 如果无法获取用户信息，仍然发送请求（后端会返回更明确的错误信息）
    return axios.post<Budget>('/api/budgets/copy', data);
  }
}; 
